/**
 * 主体节点面板（P2-主体）。顶部主体专属头（类别/名称/描述/一致性锁定），
 * 下方复用共享的 ImageGenControls——与图像卡同一套模型/参数/成本/提示词三态/负向/悬停工具栏
 * （用户 2026-10-02 要求主体复用图像逻辑）。额外给一个「生成三视图」动作。
 */
import { useState } from 'react';
import { Layers, Lock, Plus, Unlock, X } from 'lucide-react';

import type { CanvasNode, GenerationParams, MediaRef } from '../../model/canvas';
import {
  SUBJECT_CATEGORY_LABELS,
  addLook,
  ensureLooks,
  removeLook,
  renameLook,
  setActiveLook,
  type SubjectPayload,
} from '../../model/subject';
import { ImageGenControls } from './ImageGenControls';
import { VoicePicker } from './VoicePicker';
import { FRAME_TEMPLATES, STYLE_GROUPS, STYLE_PRESETS } from '../../model/styles';
import type { ImageEditOp } from './generationService';

const field = 'w-full rounded-control bg-surface-base px-2.5 py-2 text-ui text-ink-primary outline-none';

interface SubjectNodePanelProps {
  node: CanvasNode;
  subject: SubjectPayload;
  generating: boolean;
  hasUpstream: boolean;
  onPatch: (patch: Partial<SubjectPayload>) => void;
  onParams: (patch: Partial<GenerationParams>) => void;
  onGenerate: () => void;
  onOptimize: () => void;
  onCompose: () => void;
  onUpload: (file: File) => void;
  onPickVariant: (media: MediaRef) => void;
  onEdit: (op: ImageEditOp) => void;
  onGenerateTurnaround: () => void;
}

export function SubjectNodePanel({
  node,
  subject,
  generating,
  hasUpstream,
  onPatch,
  onParams,
  onGenerate,
  onOptimize,
  onCompose,
  onUpload,
  onPickVariant,
  onEdit,
  onGenerateTurnaround,
}: SubjectNodePanelProps) {
  const norm = ensureLooks(subject);
  const active = norm.looks.find((l) => l.id === norm.activeLookId) ?? norm.looks[0];
  const [voiceOpen, setVoiceOpen] = useState(false);
  return (
    <div className="flex flex-col gap-2">
      {/* 主体专属头：类别（建卡即定、只读）+ 一致性锁定 */}
      <div className="flex items-center gap-2">
        <span className="shrink-0 text-uiSm text-ink-faint">类别</span>
        <span className="rounded-control bg-accent/15 px-2 py-1 text-uiSm font-semibold text-accent">
          {SUBJECT_CATEGORY_LABELS[subject.category]}
        </span>
        <button
          type="button"
          onClick={() => onPatch({ locked: !subject.locked })}
          title="一致性锚点：锁定后作为下游生成的标准参考"
          className={`ml-auto inline-flex items-center gap-1 rounded-control px-2 py-1 text-uiSm transition-colors ${
            subject.locked ? 'bg-accent/15 text-accent' : 'text-ink-muted hover:text-ink-primary'
          }`}
        >
          {subject.locked ? <Lock size={13} /> : <Unlock size={13} />}
          {subject.locked ? '已锁定' : '锁定一致性'}
        </button>
      </div>
      <input
        value={subject.name}
        placeholder="实体名称（如：林晚 / 出租屋 / 怀表）"
        className={field}
        onChange={(e) => onPatch({ name: e.target.value })}
      />
      {/* 角色专属：绑定音色（一个角色一条声线，音频卡继承） */}
      {subject.category === 'character' && (
        <div className="flex items-center gap-2 text-uiSm">
          <span className="shrink-0 text-ink-faint">音色</span>
          <span className="truncate text-ink-primary">{subject.voice?.name ?? '未绑定（用默认）'}</span>
          {subject.voice !== undefined && (
            <span className="text-ink-faint">· {subject.voice.gender} {subject.voice.age}</span>
          )}
          <button
            type="button"
            className="ml-auto text-accent hover:underline"
            onClick={() => setVoiceOpen(true)}
          >
            选择音色
          </button>
        </div>
      )}
      {/* 角色小传（P-C3）：补人设、可驱动造声 */}
      {subject.category === 'character' && (
        <div className="flex flex-col gap-1">
          <span className="text-uiSm text-ink-faint">角色小传（可选，用于造声与立体人设）</span>
          <textarea
            value={subject.bio ?? ''}
            placeholder="背景/经历/性格…（音色库点“用小传”可据此造声）"
            className={`${field} h-14 shrink-0 resize-none`}
            onChange={(e) => onPatch({ bio: e.target.value })}
          />
        </div>
      )}
      {/* 质感风格（P-C1）：选中后后缀追加到生成提示词 */}
      <div className="flex items-center gap-2 text-uiSm">
        <span className="shrink-0 text-ink-faint">风格</span>
        <select
          className="min-w-0 flex-1 rounded-control bg-surface-base px-2 py-1 text-uiSm text-ink-primary outline-none"
          value={subject.style ?? ''}
          onChange={(e) => onPatch({ style: e.target.value })}
        >
          <option value="">无（默认）</option>
          {STYLE_GROUPS.map((g) => (
            <optgroup key={g} label={g}>
              {STYLE_PRESETS.filter((s) => s.group === g).map((s) => (
                <option key={s.id} value={s.id}>
                  {s.name}
                </option>
              ))}
            </optgroup>
          ))}
        </select>
      </div>
      <div className="flex items-center gap-2">
        <span className="text-uiSm text-ink-faint">描述</span>
        <button
          type="button"
          className="ml-auto text-uiSm text-accent hover:underline"
          title="按类别填入结构化填空模板"
          onClick={() => onPatch({ description: FRAME_TEMPLATES[subject.category] })}
        >
          填入框架
        </button>
      </div>
      <textarea
        value={subject.description}
        placeholder="外观 / 服装 / 材质 / 氛围…（点下方“生成prompt”会用它拼提示词）"
        className={`${field} h-16 shrink-0 resize-none`}
        onChange={(e) => onPatch({ description: e.target.value })}
      />

      {/* 多造型（每卡多造型/多视图，§十）：造型 tab + 当前造型重命名 */}
      <div className="flex flex-wrap items-center gap-1.5">
        {norm.looks.map((l) => (
          <div
            key={l.id}
            role="button"
            tabIndex={0}
            onClick={() => onPatch(setActiveLook(subject, l.id))}
            className={`group inline-flex cursor-pointer items-center gap-1 rounded-control px-2 py-1 text-uiSm transition-colors ${
              l.id === norm.activeLookId ? 'bg-accent/15 font-semibold text-accent' : 'text-ink-muted hover:text-ink-primary'
            }`}
          >
            <span>{l.name}</span>
            {l.viewNodeIds.length > 0 && <span className="text-ink-faint">·{l.viewNodeIds.length}</span>}
            {norm.looks.length > 1 && (
              <button
                type="button"
                title="删除造型"
                onClick={(e) => {
                  e.stopPropagation();
                  onPatch(removeLook(subject, l.id));
                }}
                className="hidden text-ink-faint hover:text-ink-primary group-hover:inline-flex"
              >
                <X size={12} />
              </button>
            )}
          </div>
        ))}
        <button
          type="button"
          title="新增造型"
          onClick={() => onPatch(addLook(subject, { id: `look-${Date.now().toString(36)}` }))}
          className="inline-flex items-center gap-1 rounded-control px-2 py-1 text-uiSm text-ink-muted transition-colors hover:text-ink-primary"
        >
          <Plus size={13} />
          造型
        </button>
      </div>
      {active !== undefined && (
        <input
          value={active.name}
          placeholder="造型名称（如：默认 / 战斗装 / 便服）"
          className={field}
          onChange={(e) => onPatch(renameLook(subject, active.id, e.target.value))}
        />
      )}
      <p className="text-uiSm leading-snug text-ink-faint">
        “生成prompt”=用【名称+外观描述】拼成提示词（不依赖上游）；从导演剧本自动抽取实体、把角色/场景描述带入这里，在下一刀（刀3）接入。
      </p>

      {/* 复用图像生成控件；额外挂「生成三视图」 */}
      <ImageGenControls
        node={node}
        generating={generating}
        hasUpstream={hasUpstream}
        onParams={onParams}
        onGenerate={onGenerate}
        onOptimize={onOptimize}
        onCompose={onCompose}
        onUpload={onUpload}
        onPickVariant={onPickVariant}
        onEdit={onEdit}
        generateLabel="生成参考图"
        composeHint="用上方名称+外观描述拼提示词"
        extraActions={
          <>
            <span className="text-ink-faint">·</span>
            <button
              type="button"
              className="inline-flex items-center gap-1 font-semibold text-accent hover:underline disabled:opacity-50"
              disabled={generating}
              onClick={onGenerateTurnaround}
            >
              <Layers size={14} />
              {subject.category === 'scene' ? '生成多视角' : subject.category === 'prop' ? '生成多角度' : '生成视图组'}
            </button>
          </>
        }
      />
      <VoicePicker
        open={voiceOpen}
        current={subject.voice}
        bioHint={subject.bio !== undefined && subject.bio !== '' ? `${subject.name}：${subject.bio}` : subject.name}
        onClose={() => setVoiceOpen(false)}
        onPick={(v) => onPatch({ voice: v })}
      />
    </div>
  );
}
